import { LocationStrategy } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { MuiIconFont } from '@move/ui';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MmuUserRoles, MmuUserType } from '../../auth/models';
import { FeatureFlag } from '../../feature-flags';
import { MmuMainMenuService } from '../main-menu-service/mmu-main-menu.service';


interface MmuMainMenuBaseEntry {
  /** The label that is displayed on the menu entry */
  label: string;

  /** Absolute URL on the current domain, e.g. /user/support/contact */
  link: string;

  /** Is the link external (will use hyperlink) or internal (will use router navigation) */
  isExternalLink?: boolean;

  /** Is the checking for active url matching state, use 'true' or leave empty for subset checking */
  exactUrlMatch?: 'exact' | 'subset';

  /** Roles that are allowed to access this menu entry. If defined, other roles will not see it at all. */
  roles?: MmuUserRoles[];

  /**
   * Make menu entry only visible if a given feature flag is enabled.
   *
   * If multiple features are defined in an array, at least one of those features must be enabled (logical OR).
   * */
  feature?: FeatureFlag | Array<FeatureFlag>;

  /**
   * Make menu entry hidden if a given feature flag is enabled.
   *
   * If multiple features are defined in an array and at least one of those features is enabled (logical OR), the entry is hidden.
   *
   * If both feature and hideFeature are defined and resolve to true, the entry is hidden (hideFeature has priority).
   *
   * @example
   * // Entry is hidden if featureA is enabled
   * {
   *   feature: 'featureA'
   *   hideFeature: 'featureA',
   *   // ...
   * }
   */
  hideFeature?: FeatureFlag | Array<FeatureFlag>;
}

export interface MmuMainMenuMainEntry extends MmuMainMenuBaseEntry {
  icon?: string;
  iconFont?: MuiIconFont;
  children?: MmuMainMenuChildEntry[];
  hidden?: string;

  /** Track Navigation events in the MmuTrackMainMenuDirective */
  tracked?: boolean;
}

export type MmuMainMenuChildEntry = MmuMainMenuBaseEntry;

@Component({
  selector: 'mmu-main-menu',
  templateUrl: './mmu-main-menu.component.html',
  styleUrls: ['./mmu-main-menu.component.scss']
})
export class MmuMainMenu implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  appTitle = '';

  @Input()
  profileMenuItems: MmuMainMenuMainEntry[] = [];

  @Input()
  menuItems: MmuMainMenuMainEntry[] = [];

  @Input()
  collapsible = true;

  @Input()
  collapseLabel = 'Minimize';

  @Input()
  userType: MmuUserType = 'customer';

  @Input()
  serviceLabel: string | null = null;

  @Input()
  menuItemLabelTemplate?: TemplateRef<unknown>;

  @ViewChild('navBar')
  navBar!: ElementRef;

  isScrollable = false;
  collapsed: boolean;

  @HostBinding('class.is--open')
  get mobileOpen(): boolean {
    return this._mobileOpen;
  }

  @HostBinding('class.is--service-user')
  get isServiceUser(): boolean {
    return this.userType === 'service';
  }

  @HostBinding('class.is--collapsed')
  get menuIsCollapsed(): boolean {
    return this.collapsed && !this.mobileOpen;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScrollability();
  }

  private _mobileOpen = false;
  private destroy$ = new Subject<void>();
  private scrollInterval?: ReturnType<typeof setTimeout>;

  constructor(
    public menuService: MmuMainMenuService,
    private _locationStrategy: LocationStrategy,
    private _router: Router,
    private renderer: Renderer2,
    private el: ElementRef
  ) {
    this.collapsed = this.menuService.getCollapsedState();
  }

  ngAfterViewInit(): void {
    this.checkScrollability();
  }

  ngOnInit(): void {
    /* It is required to bind the Observables manually here because the HostBinding decorator does not support the usage of
     *  the async pipe. Remember to disconnect the subscription in ngOnDestroy. */
    this.menuService.mobileOpen$.pipe(takeUntil(this.destroy$)).subscribe((isOpen) => (this._mobileOpen = isOpen));

    this.menuService.collapsed$.pipe(takeUntil(this.destroy$)).subscribe((isCollapsed) => {
      this.collapsed = isCollapsed;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isActiveLink(item: MmuMainMenuBaseEntry): boolean {
    let url = item.link;

    if (!item.isExternalLink) {
      url = url.replace(this._locationStrategy.getBaseHref(), '/');
    }

    return (
      this._router.isActive(url, {
        paths: item.exactUrlMatch ?? 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored'
      }) || this.isChildActive(item)
    );
  }

  private isChildActive(item: MmuMainMenuMainEntry): boolean {
    if (item?.children) {
      return item.children.some((child) => this.isActiveLink(child));
    }
    return false;
  }

  toggleMenu(event: Event): void {
    const targetElement = event.target as HTMLElement;
    targetElement.parentElement?.classList.toggle('is--open');
    this.closeOtherOpenedMenuItems(event);
  }

  toggleCollapseState(): void {
    this.menuService.toggleCollapseState();
    setTimeout(() => {
      this.checkScrollability();
    }, 0);
  }

  closeOtherOpenedMenuItems(event: Event): void {
    const expandedElements = this.el.nativeElement.querySelectorAll('.is--open');
    expandedElements.forEach((element: HTMLElement) => {
      const targetElement = event.target as HTMLElement;

      if (element !== targetElement.parentElement) {
        element.classList.remove('is--open');
      }
    });
  }

  onMouseOver(item: MmuMainMenuMainEntry, event: Event): void {
    if (item.children && item.children.length > 0 && this.collapsed) {
      const targetElement = event.target as HTMLElement;
      const subMenu = targetElement.querySelector('.mui-main-navigation__submenu');

      if (!subMenu) {
        return;
      }

      if (window.innerHeight <= subMenu.getBoundingClientRect().height) {
        this.renderer.setStyle(subMenu, 'top', '0');
        this.renderer.setStyle(subMenu, 'bottom', '0');
        return;
      }

      if (window.innerHeight > subMenu.getBoundingClientRect().bottom) {
        const parentMenuTopPosition = targetElement.getBoundingClientRect().top;
        this.renderer.setStyle(subMenu, 'top', `${parentMenuTopPosition}px`);
      } else {
        const divBottomPosition = window.innerHeight - targetElement.getBoundingClientRect().bottom;
        const targetHeight = subMenu.getBoundingClientRect().height;
        if (targetHeight > targetElement.getBoundingClientRect().bottom) {
          this.renderer.setStyle(subMenu, 'top', '0');
        } else {
          this.renderer.setStyle(subMenu, 'bottom', `${divBottomPosition}px`);
        }
      }
    }
  }

  onMouseLeave(item: MmuMainMenuMainEntry, event: Event): void {
    if (item.children && item.children.length > 0 && this.collapsed) {
      const targetElement = event.target as HTMLElement;
      const subMenu = targetElement.querySelector('.mui-main-navigation__submenu');

      this.renderer.removeStyle(subMenu, 'top');
      this.renderer.removeStyle(subMenu, 'bottom');
    }
  }

  private checkScrollability(): void {
    if (this.collapsed) {
      const scrollableContainer = this.navBar.nativeElement;
      this.isScrollable = scrollableContainer.scrollHeight > scrollableContainer.clientHeight;
    }
  }

  startScroll(direction: 'up' | 'down'): void {
    this.scrollInterval = setInterval(() => {
      this.scroll(direction);
    }, 80);
  }

  stopScroll(): void {
    clearInterval(this.scrollInterval);
  }

  private scroll(direction: 'up' | 'down'): void {
    const scrollAmount = 30;

    if (direction === 'up') {
      this.navBar.nativeElement.scrollTo({
        top: this.navBar.nativeElement.scrollTop - scrollAmount,
        behavior: 'smooth'
      });
    } else if (direction === 'down') {
      this.navBar.nativeElement.scrollTo({
        top: this.navBar.nativeElement.scrollTop + scrollAmount,
        behavior: 'smooth'
      });
    }
  }
}
